import React, { memo, useState } from 'react';
import clsx from 'clsx';
import { Button } from '../../../../components/ui/Button';
import { FilterableSelect } from '../../../../components/ui/FilterableSelect';
import TextField from './helpers/TextField';

/** Stand-in for the MUI `Switch` with its `FormControlLabel`. */
const Toggle = ({ label, checked, onChange, bold = false }) => (
  <div className="flex items-center gap-3">
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={onChange}
      className={clsx(
        'relative w-10 h-6 rounded-full transition-colors flex-shrink-0',
        checked ? 'bg-ninja-green' : 'bg-border'
      )}
    >
      <span
        className={clsx(
          'absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all',
          checked ? 'left-5' : 'left-1'
        )}
      />
    </button>
    <span className={clsx('text-sm text-ninja-dark dark:text-white', bold && 'font-bold')}>
      {label}
    </span>
  </div>
);

const Meli = ({
  ecommerce,
  warehouses,
  onSave,
  loading,
  saving = false,
  isEdit,
  onConnect,
}) => {
  const [formData, setFormData] = useState(ecommerce);
  const [localError, setLocalError] = useState("");
  const [connecting, setConnecting] = useState(false);

  const handleWarehouseChange = (value) => {
    setFormData({ ...formData, warehouse: value });
  };

  const handleToggle = (key) => {
    setFormData((prev) => ({
      ...prev,
      configuration: {
        ...prev.configuration,
        options: {
          ...prev.configuration.options,
          [key]: !prev.configuration.options[key],
        },
      },
    }));
  };

  const handleConnectClick = async () => {
    setLocalError("");
    setConnecting(true);

    try {
      const { success, redirectUri, error } = await onConnect(formData);

      if (success && redirectUri) {
        // Still busy until the browser leaves: a second click would start
        // another authorization, and the backend allows one per warehouse.
        window.location.replace(redirectUri);
        return;
      }
      setLocalError(error || "No se pudo obtener la URL de autorización.");
    } catch (error) {
      console.error(error);
      setLocalError("Error inesperado al intentar conectar.");
    }
    setConnecting(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const isConnected =
    !!formData.configuration.authentication.integration_user_id;

  return (
    <form data-testid="ecommerce-form" data-platform="meli">
      <h3 className="text-lg font-bold text-ninja-dark dark:text-white mb-4">
        Configuración Mercado Libre
      </h3>

      <div className="bg-blue-50 dark:bg-blue-900/20 text-info p-3 rounded-xl border border-blue-200 dark:border-blue-900 text-sm mb-5">
        Vincula tu cuenta para sincronizar ventas y etiquetas. Configura qué
        tipos de envío quieres gestionar.
      </div>

      {localError && (
        <div className="bg-red-50 dark:bg-red-900/20 text-danger p-3 rounded-xl border border-red-200 dark:border-red-900 text-sm mb-4">
          {localError}
        </div>
      )}

      <div className="mb-5">
        <FilterableSelect
          variant="form"
          testId="ecommerce-warehouse"
          label="Bodega por defecto"
          value={formData.warehouse || ""}
          onChange={handleWarehouseChange}
          options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
          placeholder="Seleccionar..."
        />
      </div>

      <div className="mb-5 text-center">
        {!isConnected ? (
          <Button
            type="button"
            variant="primary"
            size="lg"
            fullWidth
            onClick={handleConnectClick}
            disabled={loading}
            loading={connecting}
            loadingLabel="Redirigiendo..."
          >
            Vincular cuenta de Mercado Libre
          </Button>
        ) : (
          <div className="w-full py-3 rounded-full border border-ninja-green text-ninja-green font-semibold">
            {`Conectado (User ID: ${formData.configuration.authentication.integration_user_id})`}
          </div>
        )}
      </div>

      {isConnected && (
        <>
          <p className="text-sm font-bold text-ninja-dark dark:text-white mb-3">
            Configuración de Envíos
          </p>

          <div className="grid grid-cols-2 gap-4 mb-5 border border-border rounded-xl p-4">
            <Toggle
              label="Fulfillment (Full)"
              checked={formData.configuration.options.fulfillment || false}
              onChange={() => handleToggle("fulfillment")}
            />
            <Toggle
              label="Flex"
              checked={formData.configuration.options.self_service || false}
              onChange={() => handleToggle("self_service")}
            />
            <Toggle
              label="XD Drop OFF"
              checked={formData.configuration.options.xd_drop_off || false}
              onChange={() => handleToggle("xd_drop_off")}
            />
            <Toggle
              label="Cross Docking"
              checked={formData.configuration.options.cross_docking || false}
              onChange={() => handleToggle("cross_docking")}
            />
            <Toggle
              label="Turbo"
              checked={formData.configuration.options.turbo || false}
              onChange={() => handleToggle("turbo")}
            />
            <Toggle
              label="Personalizado"
              checked={formData.configuration.options.custom || false}
              onChange={() => handleToggle("custom")}
            />
            <div className="col-span-2 h-px bg-border" />
            <div className="col-span-2">
              <Toggle
                label="Imprimir etiquetas automáticamente desde MELI"
                checked={formData.configuration.options.automatic_print_label || false}
                onChange={() => handleToggle("automatic_print_label")}
                bold
              />
            </div>
          </div>

          <p className="text-sm font-bold text-ninja-dark dark:text-white mb-3">
            Tokens de Acceso
          </p>
          <div className="space-y-4 mb-5">
            <TextField
              label="Access Token"
              value={formData.configuration.authentication.token || ""}
              disabled
            />
            <TextField
              label="Refresh Token"
              value={formData.configuration.authentication.refresh_token || ""}
              disabled
            />
            <TextField
              label="User ID Integración"
              value={formData.configuration.authentication.integration_user_id || ""}
              disabled
            />
          </div>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            disabled={loading}
            loading={saving}
            loadingLabel="Actualizando..."
            onClick={handleSubmit}
          >
            Actualizar Configuración
          </Button>
        </>
      )}
    </form>
  );
};

export default memo(Meli);
